import { CommonModule, isPlatformBrowser } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  Input,
  PLATFORM_ID,
} from "@angular/core";
import { ChartModule } from "primeng/chart";
import { SharedService } from "../../../../../core/services/shared.service";
import { QR2ReportsDto } from "../../../../../core/models/dashboard/qr2ReportsDto";
import { QR1ReportsDto } from "../../../../../core/models/dashboard/qr1ReportsDto";

@Component({
  selector: "jni-multi-line-chart",
  imports: [ChartModule, CommonModule],
  templateUrl: "./multi-line-chart.component.html",
  styleUrl: "./multi-line-chart.component.scss",
})
export class MultiLineChartComponent implements AfterViewInit {
  @Input() type = "";
  @Input() recordList: QR2ReportsDto[] | QR1ReportsDto[] = [];
  @Input() title = "";
  @Input() xAxisTitle = "";
  @Input() yAxisTitle = "";

  data!: {
    labels: (string | undefined)[];
    datasets: {
      label?: string;
      backgroundColor?: string | string[];
      borderColor?: string;
      hoverBackgroundColor?: string | string[];
      data?: number[];
      type?: string;
      borderWidth?: number;
      fill?: boolean;
      tension?: number;
    }[];
  };
  options!: {
    maintainAspectRatio: boolean;
    aspectRatio: number;
    responsive: boolean;
    plugins: {
      legend: {
        position: string;
        labels: {
          color: string;
        };
      };
      tooltip?: {
        mode: string;
        intersect: boolean;
      };
      title?: {
        display: boolean;
        text: string;
        font: { size: number; weight: string };
      };
    };
    scales: {
      x: {
        ticks: {
          color: string;
        };
        grid: {
          color: string;
          drawBorder: boolean;
        };
        title: {
          display: boolean;
          text: string;
          font: { size: number; weight: string };
        };
      };
      y: {
        ticks: {
          color: string;
        };
        title: {
          display: boolean;
          text: string;
          font: { size: number; weight: string };
        };
        grid: {
          color: string;
          drawBorder: boolean;
        };
        beginAtZero: boolean;
      };
    };
  };

  platformId = inject(PLATFORM_ID);

  bgColor!: string;

  private sharedService = inject(SharedService);

  constructor(private cd: ChangeDetectorRef) {
    effect(() => {
      const currentTheme = this.sharedService.getTheme()();
      this.bgColor = currentTheme === "dark" ? "#212630" : "#FFFFFF";
      this.ngAfterViewInit();
    });
  }

  ngAfterViewInit(): void {
    this.initChart();
  }

  initChart() {
    if (isPlatformBrowser(this.platformId)) {
      const documentStyle = getComputedStyle(document.documentElement);
      const textColor = documentStyle.getPropertyValue("--p-h2color-color");
      const textColorSecondary =
        documentStyle.getPropertyValue("--p-h2color-color");
      const surfaceBorder = documentStyle.getPropertyValue("--bs-border-color");

      if (this.type === "orderWritten") {
        this.orderWrittenDataSetup();
      } else if (this.type === "customerAging") {
        const isQR1Report = (
          items: QR2ReportsDto[] | QR1ReportsDto[]
        ): items is QR1ReportsDto[] => {
          return items.length === 0 || "client" in items[0];
        };
        if (isQR1Report(this.recordList)) {
          this.customerAgingDataSetup(this.recordList);
        }
      }

      this.options = {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        responsive: true,
        plugins: {
          legend: {
            position: "top",
            labels: {
              color: textColor,
            },
          },
          tooltip: { mode: "index", intersect: false },
          title: {
            display: true,
            text: this.title,
            font: { size: 18, weight: "bold" },
          },
        },
        scales: {
          x: {
            ticks: {
              color: textColorSecondary,
            },
            grid: {
              color: surfaceBorder,
              drawBorder: false,
            },
            title: {
              display: true,
              text: this.xAxisTitle,
              font: { size: 16, weight: "bold" },
            },
          },
          y: {
            ticks: {
              color: textColorSecondary,
            },
            grid: {
              color: surfaceBorder,
              drawBorder: false,
            },
            title: {
              display: true,
              text: this.yAxisTitle,
              font: { size: 16, weight: "bold" },
            },
            beginAtZero: true,
          },
        },
      };
      this.cd.markForCheck();
    }
  }

  orderWrittenDataSetup() {
    const isQR2Report = (
      item: QR2ReportsDto | QR1ReportsDto
    ): item is QR2ReportsDto => {
      return "orderDate" in item;
    };
    const uniqueDates = Array.from(
      new Set(
        this.recordList.filter(isQR2Report).map(o => o.orderDate?.split("T")[0])
      )
    ).sort();

    const statuses = ["Open order", "Delivered", "Invoiced"];

    const datasets = statuses.map(status => {
      const counts = uniqueDates.map(
        date =>
          this.recordList.filter(
            o =>
              isQR2Report(o) &&
              o.orderDate?.startsWith(date ?? "") &&
              o.stat === status
          ).length
      );

      return {
        label: status,
        data: counts,
        fill: false,
        tension: 0.3, // smooth curve
      };
    });

    this.data = {
      labels: uniqueDates,
      datasets: datasets,
    };
  }

  customerAgingDataSetup(data: QR1ReportsDto[]) {
    const grouped: Record<
      string,
      {
        clientId: string;
        client: string;
        totalAmount: number;
        openAmount: number;
        payments: number;
      }
    > = {};

    data.forEach(item => {
      if (!grouped[item.clientId]) {
        grouped[item.clientId] = {
          clientId: item.clientId,
          client: item.client,
          totalAmount: 0,
          openAmount: 0,
          payments: 0,
        };
      }

      grouped[item.clientId].totalAmount += item.totalAmount || 0;
      grouped[item.clientId].openAmount += item.openAmount || 0;
      grouped[item.clientId].payments += item.payments || 0;
    });

    const groupedArray = Object.values(grouped)
      .sort((a, b) => b.totalAmount - a.totalAmount)
      .slice(0, 10);

    // Chart labels (client names)
    const labels = groupedArray.map(item =>
      item.client.length > 15
        ? `${item.client.substring(0, 15)}...`
        : item.client
    );
    // Chart datasets
    const datasets = [
      {
        label: "Total Amount",
        data: groupedArray.map(item => item.totalAmount),
        fill: false,
        tension: 0.3,
      },
      {
        label: "Open Amount",
        data: groupedArray.map(item => item.openAmount),
        fill: false,
        tension: 0.3,
      },
      {
        label: "Payments",
        data: groupedArray.map(item => item.payments),
        fill: false,
        tension: 0.3,
      },
    ];

    this.data = {
      labels: labels,
      datasets: datasets,
    };
  }
}
